<template>
    <div class="contact-manager">
        <div class="tile-title">
            <h3>{{ label }}</h3>
            <v-popper2
                v-if="editable"
                ref="popper"
                class="no-flex"
                position="bottomEnd"
                :offset="8"
                :auto-hide="false">
                <template #trigger="{popper}">
                    <r-button v-popper-trigger type="link" @click="fetchData(popper)">Manage</r-button>
                </template>
                <template #content>
                    <div class="popover">
                        <div class="popover-title">
                            <h2>Manage Address</h2>
                            <contact-form-modal
                                title="Add New"
                                class="no-flex"
                                :custom-fields="customFields"
                                @input="addContact"
                                :canDelete="false"/>
                        </div>
                        <div v-if="!isDataReady" class="popover-control inset-s">
                            <div class="position-relative" style="min-height: 36px">
                                <v-inline-loader/>
                            </div>
                        </div>
                        <div v-else class="popover-control" :class="{'is-scrollable' : scrollableContacts }">
                            <template v-for="contact in contactsList">
                                <div class="popover-content"
                                     :class="{'popover-content-secondary': isActiveContact(contact)}">
                                    <div class="grid-fitted grid-unstackable">
                                        <div class="grid-item">
                                            <v-contact class="stack-m" :contact="contact"/>
                                            <slot
                                                name="list-action"
                                                :contact="contact"
                                                :is-active="isActiveContact(contact)"/>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </template>
            </v-popper2>
        </div>
        <div class="tile-content">
            <v-contact v-if="address" :emailActions="emailActions" @email-sent="$emit('email-sent')" :contact="address"/>
            <p v-else class="text-muted">No contact information available.</p>
        </div>
    </div>
</template>

<script>
    import {vIcon} from '@/components/form/index';
    import vPopper2 from '@/components/popper/v-popper2';
    import vInlineLoader from '@/components/v-inline-loader';
    import vContact from '@/components/v-contact';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import ContactModel from '@/common/models/contact-model';
    import ContactFormModal from './contact-form-modal';

    export default {
        name: 'contact-manager',
        components: {
            vInlineLoader,
            vIcon,
            vPopper2,
            vContact,
            ContactFormModal,
        },
        mixins: [LoadStateMixin],
        props: {
            label: {
                type: String,
                required: true,
            },
            editable: {
                type: Boolean,
                default: true,
            },
            address: {
                type: [Object, null],
            },
            customerId: {
                type: String,
                required: true,
            },
            emailActions: {
                type: Boolean,
            },
        },
        data() {
            return {
                contacts: null,
                customFields: null,
            };
        },
        computed: {
            scrollableContacts() {
                return this.contacts && this.contacts.length > 1;
            },
            contactsList() {
                if (!this.contacts || !this.address) {
                    return [];
                }

                const active = this.address.hash;
                // eslint-disable-next-line no-confusing-arrow
                return this.contacts.sort(contact => contact.hash === active ? -1 : 1);
            },
        },
        methods: {
            isActiveContact(contact) {
                return contact.hash === this.address.hash;
            },
            async fetchData(popper = null) {
                if (popper) {
                    popper.toggle();
                }
                this.isDataReady = false;
                const contactsParam = {
                    filter: `customerId:${this.customerId}`,
                };
                const contactCustomFieldsParam = {
                    resource: 'contacts',
                };
                try {
                    const [
                        {items: contactsItems},
                        {items: contactsCustomFields},
                    ] = await this.$api(({contacts, customFields}) => Promise.all([
                        contacts.getAll(contactsParam),
                        customFields.getAll(contactCustomFieldsParam),
                    ]));
                    this.contacts = contactsItems.map(({fields}) => new ContactModel(fields));
                    this.customFields = contactsCustomFields;
                    this.isDataReady = true;
                } catch (error) {
                    this.isDataReady = false;
                    this.$toast.negative('Unable to load the customer contacts.');
                    throw error;
                }
            },
            addContact(contact) {
                console.log('addContact', contact);
            },
        },
    };
</script>
